<script setup lang="ts">
import { computed } from 'vue';
import { useGetContractDetails } from '@/api/forecasttrader.queries';
import { useGetEventContractBidAsk } from '@/api/mdfarm.queries';
import BetSlip from './BetSlip.vue';
import BetSlipSkeleton from './BetSlipSkeleton.vue';
import BetSlipError from './BetSlipError.vue';
import { getPartyColor, createBidPriceChips } from './utils';
import type { BetSlipProps, BetSlipItem } from '@/types/betslip.types';

const props = defineProps<{
  conid: number;
}>();

const emit = defineEmits<{
  'header-click': [];
  'item-click': [item: BetSlipItem];
}>();

// Fetch contract details (static data)
const contractQuery = useGetContractDetails(computed(() => ({ conid: props.conid })));

// Fetch live prices for yes contract (polls every 7s when visible)
const yesBidAsk = useGetEventContractBidAsk(
  computed(() => contractQuery.data.value?.conid_yes),
  'FORECASTX',
);

// Fetch live prices for no contract (polls every 7s when visible)
const noBidAsk = useGetEventContractBidAsk(
  computed(() => contractQuery.data.value?.conid_no),
  'FORECASTX',
);

// Combined loading state - true if contract query is loading
const isLoading = computed(() => contractQuery.isLoading.value);

// Combined error state
const isError = computed(() => contractQuery.isError.value);

// All data ready (contract loaded, prices optional)
const isReady = computed(() => !!contractQuery.data.value);

// Transform to BetSlipProps
const betSlipProps = computed<BetSlipProps | null>(() => {
  if (!contractQuery.data.value) return null;

  const contract = contractQuery.data.value;

  return {
    title: contract.question,
    items: [
      {
        id: contract.conid_yes,
        title: contract.strike_label,
        titleColor: getPartyColor(contract.party),
        link: `/#/election-hub?eh_view=sharecontracts&eh_conid=${props.conid}`,
        // image: `/tws.proxy/public/icons/forecastx?conid=${contract.conid_yes}&fallback=true`,
        chips: createBidPriceChips(yesBidAsk.data.value?.bid, noBidAsk.data.value?.bid),
      },
    ],
  };
});

// Refetch all queries
function refetchAll() {
  contractQuery.refetch();
}

function handleHeaderClick() {
  emit('header-click');
}

function handleItemClick(item: BetSlipItem) {
  emit('item-click', item);
}

// Expose computed data for parent components (e.g., ShareBetSlipDialog)
defineExpose({
  betSlipProps,
  marketName: computed(() => contractQuery.data.value?.question || 'Election Contract'),
  betSlipLink: computed(() => `/#/election-hub?eh_view=sharecontracts&eh_conid=${props.conid}`),
});
</script>

<template>
  <BetSlipSkeleton v-if="isLoading" />
  <BetSlipError v-else-if="isError" @retry="refetchAll" />
  <BetSlip
    v-else-if="isReady && betSlipProps"
    v-bind="betSlipProps"
    @header-click="handleHeaderClick"
    @item-click="handleItemClick"
  />
</template>
